import type { SenderRule } from "./api";

/**
 * Так ядро описывает основание, по которому отправитель письма отнесён к
 * контрагенту. Ключи совпадают со значениями `sender_resolution_method` и
 * `match_method`.
 */
export const resolutionMethodLabels: Readonly<Record<string, string>> = {
  manual: "Выбрано вручную",
  saved_email: "Сохранённое правило по адресу",
  contact_email: "Email из контактов контрагента",
  saved_domain: "Сохранённое правило по домену",
  catalog_domain: "Домен из карточки контрагента",
  saved_pattern: "Сохранённое правило по шаблону",
  inn: "ИНН (устаревший способ)",
  kpp: "КПП (устаревший способ)",
  // Письма, определённые по названию, ИНН или КПП раньше, сохранили этот способ
  // в истории; новых таких определений нет: эти данные пишет сам отправитель.
  exact_name: "Название (устаревший способ)",
  unresolved: "Отправитель не найден",
  conflict: "Конфликт",
};

export function resolutionMethodLabel(method: string | null): string {
  if (method === null) return "Не определён";
  return resolutionMethodLabels[method] ?? method;
}

/**
 * Что учитывается при определении отправителя, от самого сильного основания к
 * самому слабому. Побеждает самое сильное основание; если оно подошло сразу
 * нескольким контрагентам, автоматически ничего не выбирается — письмо
 * получает статус «конфликт» и ждёт решения человека.
 *
 * Числа, которыми Core считает силу основания, человеку не показываем: важен
 * порядок, а не значения.
 */
export const resolutionLadder: readonly {
  readonly key: string;
  readonly label: string;
  readonly hint: string;
}[] = [
  {
    key: "manual",
    label: "Ваше ручное решение",
    hint: "Письмо, которое вы уже сопоставили сами, больше не пересматривается.",
  },
  {
    key: "saved_email",
    label: "Правило по адресу",
    hint: "Сохранённое правило для точного email отправителя.",
  },
  {
    key: "contact_email",
    label: "Контакт контрагента",
    hint: "Такой email указан среди контактов контрагента.",
  },
  {
    key: "saved_domain",
    label: "Правило по домену",
    hint: "Сохранённое правило для всех адресов с этого домена.",
  },
  {
    key: "catalog_domain",
    label: "Домен из карточки",
    hint: "Домен указан в карточке контрагента.",
  },
];

export const matchTypeLabels: Readonly<
  Record<SenderRule["match_type"], string>
> = {
  email: "Адрес",
  domain: "Домен",
  pattern: "Шаблон",
};

/**
 * Важность правила простыми словами. Она нужна только тогда, когда к одному
 * отправителю подходят сразу два правила одного вида: побеждает более важное,
 * а при равной важности письмо попадает в «конфликт».
 *
 * Core хранит число от 0 (самое сильное), но при определении учитывает его не
 * выше девятки, поэтому три уровня передают всё, что на самом деле различается.
 */
export type RuleImportance = "high" | "normal" | "low";

export const importanceLabels: Readonly<Record<RuleImportance, string>> = {
  high: "Высокая",
  normal: "Обычная",
  low: "Низкая",
};

/** Число, с которым правило создаётся на выбранном уровне. */
export const importanceValues: Readonly<Record<RuleImportance, number>> = {
  high: 0,
  normal: 5,
  low: 9,
};

export function ruleImportance(priority: number): RuleImportance {
  if (priority <= 0) return "high";
  return priority >= 9 ? "low" : "normal";
}
